import { useCallback, useState } from 'react';
import { Alert, Button } from '../design/index.js';
import type { Failure } from '../lib/failure.js';

/**
 * What a screen shows when what it came to show could not be fetched.
 *
 * **Instead of the content, never beside it.** The failure this replaces was
 * an error above an empty state — « Impossible d’afficher les appels » over
 * « Aucun appel pour le moment » — which tells a salon owner two contradictory
 * things and prints the reassuring one larger. Or an error above skeletons
 * that went on pulsing, which says the answer is still on its way when it is
 * not coming. Or above the previous week's appointments, under this week's
 * dates.
 *
 * « Réessayer » asks again in place, without reloading the page and whatever
 * else is on it, and appears only when asking again could change the answer
 * (`worthRetrying`): a refusal for a role comes back the same every time.
 */
export function LoadFailure({
  title,
  failure,
  onRetry,
}: {
  /** What could not be shown, as a sentence about this screen. */
  readonly title: string;
  readonly failure: Failure;
  readonly onRetry: () => void;
}): React.JSX.Element {
  return (
    <Alert
      kind="error"
      title={title}
      {...(failure.retry && {
        action: (
          <Button variant="secondary" size="sm" onClick={onRetry}>
            Réessayer
          </Button>
        ),
      })}
    >
      {failure.message}
    </Alert>
  );
}

/**
 * A number for a load's effect to depend on, and the function that asks again.
 *
 * Asking again is running the effect again, so it goes through the same path
 * as the first time — skeleton, request, answer — and cannot drift from it.
 */
export function useAttempt(): readonly [number, () => void] {
  const [attempt, setAttempt] = useState(0);
  const again = useCallback(() => {
    setAttempt((current) => current + 1);
  }, []);
  return [attempt, again];
}
